/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * LanguageToggle Component
 * ============================================================================
 *
 * Toggle button for switching between English and French languages.
 * Displays flags and language codes.
 *
 * ============================================================================
 */

import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { languages, type LanguageCode } from '@/lib/i18n';

interface LanguageToggleProps {
  /** Size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Show flags instead of text */
  showFlags?: boolean;
  /** Additional class names */
  className?: string;
}

export function LanguageToggle({
  size = 'md',
  showFlags = true,
  className = '',
}: LanguageToggleProps) {
  const { i18n } = useTranslation('common');
  const currentLang = i18n.language as LanguageCode;

  const toggleLanguage = () => {
    const newLang = currentLang === 'en' ? 'fr' : 'en';
    i18n.changeLanguage(newLang);
  };

  const currentLanguage = languages.find((l) => l.code === currentLang) || languages[0];
  const nextLanguage = languages.find((l) => l.code !== currentLang) || languages[1];

  const sizeStyles = {
    sm: {
      button: 'px-2 py-1 text-xs gap-1',
      icon: 14,
    },
    md: {
      button: 'px-3 py-2 text-sm gap-2',
      icon: 16,
    },
    lg: {
      button: 'px-4 py-2.5 text-base gap-2',
      icon: 18,
    },
  };

  const styles = sizeStyles[size];

  return (
    <>
      <button
        type="button"
        onClick={toggleLanguage}
        className={`language-toggle ${styles.button} ${className}`}
        title={`Switch to ${nextLanguage.name}`}
        aria-label={`Current language: ${currentLanguage.name}. Click to switch to ${nextLanguage.name}`}
      >
        {showFlags ? (
          <>
            <span className="language-flag">{currentLanguage.flag}</span>
            <span className="language-code">{currentLanguage.code.toUpperCase()}</span>
          </>
        ) : (
          <>
            <Globe size={styles.icon} />
            <span className="language-code">{currentLanguage.code.toUpperCase()}</span>
          </>
        )}
      </button>

      <style>{`
        .language-toggle {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.05);
          border: 1px solid rgba(255, 255, 255, 0.1);
          border-radius: var(--radius-md, 8px);
          color: var(--color-text-secondary, #94A3B8);
          font-family: var(--font-mono, monospace);
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          white-space: nowrap;
        }

        .language-toggle:hover {
          background: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.2);
          color: var(--color-text-primary, #E2E8F0);
        }

        .language-toggle:active {
          transform: scale(0.98);
        }

        [data-theme="light"] .language-toggle {
          background: rgba(0, 0, 0, 0.03);
          border-color: rgba(0, 0, 0, 0.1);
          color: var(--color-text-secondary, #64748B);
        }

        [data-theme="light"] .language-toggle:hover {
          background: rgba(0, 0, 0, 0.06);
          border-color: rgba(0, 0, 0, 0.15);
          color: var(--color-text-primary, #1E293B);
        }

        .language-flag {
          font-size: 1.2em;
          line-height: 1;
        }

        .language-code {
          letter-spacing: 0.05em;
        }
      `}</style>
    </>
  );
}

export default LanguageToggle;
